import { useEffect, useRef, useState } from 'react';
import type { BookingDto, BookingStatus } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

export interface BookingFilters {
  /** YYYY-MM-DD, or null for the server's default — today's TRADING day. */
  businessDate: string | null;
  status: BookingStatus | null;
}

export const DEFAULT_BOOKING_FILTERS: BookingFilters = { businessDate: null, status: null };

/**
 * Slower than the Device List's 10s: the board changes when staff act, not
 * continuously, and every action here reloads explicitly anyway. The poll only
 * keeps a second terminal honest.
 */
const POLL_INTERVAL_MS = 30_000;

function toQueryString(filters: BookingFilters): string {
  const params = new URLSearchParams();
  if (filters.businessDate) params.set('businessDate', filters.businessDate);
  if (filters.status) params.set('status', filters.status);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export type BookingsLoad = (options?: { quiet?: boolean }) => Promise<void>;

export interface BookingsLoaderTarget {
  setBookings: (rows: BookingDto[]) => void;
  setError: (message: string | null) => void;
  setLoading: (loading: boolean) => void;
}

/**
 * The shared load, extracted from the hook so the race below is testable in
 * plain node (the same reason wedge assembly lives in cards/wedge.ts).
 *
 * One instance serves the filter effect, the 30s poll and every action
 * reload, so responses can cross: a slow "today" reply resolving after the
 * date picker already fetched "tomorrow" would overwrite the newer board.
 * Every call takes a monotonically-increasing request id and a response only
 * lands while its id is still the latest — the same intent as
 * useReservationAlerts' cancelled flag, applied to a shared, repeatable load
 * rather than a single mount-time fetch. A superseded call also leaves
 * `loading` alone: that flag belongs to the request that is still current.
 */
export function createBookingsLoader(
  getFilters: () => BookingFilters,
  target: BookingsLoaderTarget,
): BookingsLoad {
  let latestRequestId = 0;

  return async function load(options: { quiet?: boolean } = {}): Promise<void> {
    const requestId = ++latestRequestId;
    const isCurrent = () => requestId === latestRequestId;

    if (!options.quiet) target.setLoading(true);
    try {
      const rows = await api.get<BookingDto[]>(`/bookings${toQueryString(getFilters())}`);
      if (!isCurrent()) return;
      target.setBookings(rows);
      target.setError(null);
    } catch (caught) {
      if (!isCurrent()) return;
      target.setError(caught instanceof ApiError ? caught.message : 'Could not load bookings.');
    } finally {
      if (isCurrent()) target.setLoading(false);
    }
  };
}

export function useBookings(filters: BookingFilters) {
  const [bookings, setBookings] = useState<BookingDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Created once (lazily) so the loader's latest-wins counter is shared by
  // the filter effect, the poll and the action reloads for this mount. The
  // state setters it captures are stable, and `load` is too.
  const loadRef = useRef<BookingsLoad | null>(null);
  loadRef.current ??= createBookingsLoader(() => filtersRef.current, {
    setBookings,
    setError,
    setLoading,
  });
  const load = loadRef.current;

  useEffect(() => {
    void load();
  }, [load, filters.businessDate, filters.status]);

  useEffect(() => {
    const timer = setInterval(() => void load({ quiet: true }), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return { bookings, loading, error, reload: load };
}

export interface NoShowHandlers {
  /** Copy for a failure that is not an ApiError — the caller owns the i18n. */
  fallbackMessage: string;
  /** Receives the message of every failure worth a human's attention. */
  onFailure: (message: string) => void;
  reload: () => void;
}

/**
 * Mark a booking NO_SHOW.
 *
 * Exactly one refusal is swallowed: `booking_not_open`, the genuine lost race
 * — a colleague seated or cancelled the booking first, and the reload shows
 * the current truth without a dialog. Everything else (a 500, a permission
 * refusal, the network dying) surfaces its message; swallowing those would
 * leave staff clicking a button that silently does nothing. The reload runs
 * in every path, success or failure — the board must show the server's truth.
 */
export async function markNoShow(bookingId: string, handlers: NoShowHandlers): Promise<void> {
  try {
    await api.post(`/bookings/${bookingId}/no-show`);
  } catch (caught) {
    const lostRace = caught instanceof ApiError && caught.code === 'booking_not_open';
    if (!lostRace) {
      handlers.onFailure(caught instanceof ApiError ? caught.message : handlers.fallbackMessage);
    }
  }
  handlers.reload();
}

/**
 * The presentational gate for the Device List's attention surface — ribbon,
 * banner, soft confirm (WAVE2-DESIGN §1). Read once per mount: flipping the
 * setting is a management act, not something the board must track live.
 * Defaults to alerts ON, matching the column default, and fails toward ON —
 * a venue that paid for bookings should not silently lose the nudges.
 */
export function useReservationAlerts(): boolean {
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const flags = await api.get<{ enableReservationAlerts: boolean }>('/bookings/alerts');
        if (!cancelled) setEnabled(flags.enableReservationAlerts);
      } catch {
        // Keep the default.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return enabled;
}
